import { getProductsPackaging } from "../../../../api/ECOM/getProductPackaging.api";
import { getSAPProductDetails } from "../../../../api/ECOM/getSAPProductDetails.api";
import { GetFavouriteRecipes, GetPackagingItem, SAPProductDetail } from "../../../../interfaces/ecom/schema";
import { ProductCardType, type IProductCard } from "./ProductCard.types";
import { isLoggedIn } from '../../../../data/user.model';

const createProductLink = (baseUrl: string, materialNumber?: string) => {
    return materialNumber ? `${baseUrl}?materialNumber=${materialNumber}` : baseUrl;
}

export const mapAEMProductToCardData = (product, renditionFallback = '', componentType) => {
    return {
        type: ProductCardType.PRODUCT,
        image: {
            src: product.Packagings?.[0]?.skuImageURL || product.defaultPackagingImage?.smallUrl || product.referenceImage?.smallUrl || renditionFallback,
            alt: product.referenceImage?.altText || product.title,
        },
        hoverImage: {
            src: product.referenceImage?.smallUrl,
            alt: product.referenceImage?.altText || product.title,
        },
        title: product.title,
        subtitle: product.Packagings?.[0]?.SalesUnit ? `${product.Packagings?.[0].SalesUnit} ${product.Packagings?.[0].Packaging} ${product.Packagings?.[0].BaseUnit}` : product?.Packagings?.[0]?.Packaging,
        text: product.referenceText,
        link: createProductLink(product.url, product.Packagings?.[0]?.MaterialNumber),
        hasProductFooter: true,
        hasProductPrice: product.Packagings?.[0]?.HasPrice,
        product: product,
        componentType: componentType,
        tags: product.tags,
    } as IProductCard;
};

export const mapProductToCardData = (product, renditionFallback = '', componentType) => {
    return {
        type: ProductCardType.PRODUCT,
        image: {
            src: product.skuImageURL || renditionFallback,
            alt: product.AEMProdName,
        },
        hoverImage: {
            src: product.imageURL,
            alt: product.AEMProdName,
        },
        title: product.AEMProdName,
        subtitle: product.Packagings[0]?.SalesUnit ? `${product.Packagings[0].SalesUnit} ${product.Packagings[0].Packaging} ${product.Packagings[0].BaseUnit}` : product?.Packagings[0]?.Packaging,
        text: product.AemProdShortDescription || product.AEMProdShortDescription,
        link: createProductLink(product.ProductUrl, product.MaterialNumber),
        hasProductFooter: true,
        hasProductPrice: true,
        product: product,
        componentType: componentType,
        tags: product?.Tags
    } as IProductCard;
};

export const mapRecipeToCardData = (recipe, favorites, renditionFallback = '') => {
    return {
        type: ProductCardType.RECIPE,
        image: {
            src: recipe.referenceImage?.smallUrl || renditionFallback,
            alt: recipe.referenceImage?.altText || recipe.referenceTitle,
        },
        title: recipe.referenceTitle,
        subtitle: '',
        text: '',
        hasProductFooter: false,
        hasProductPrice: false,
        product: recipe,
        hasRecipeFooter: true,
        link: recipe.url,
        tags: recipe.tags,
        isFavorite: checkIfRecipeIsFavorite(recipe, favorites),
    } as IProductCard;
};

function checkIfRecipeIsFavorite(recipe, favorites: GetFavouriteRecipes) {
    if (!favorites?.Recipes.length) { return false; }
    return favorites.Recipes.some(fr => fr.RecipeId === recipe.id);
}

export const enrichProductsWithOutSystemData = async (products) => {
    if (!products?.length) {
        return [];
    }
    const productsIds = products.map(product => product.AemProductId);
    const materialIds = products.map(({ MaterialNumber }) => MaterialNumber);
    const { Packaging: packagingResult } = isLoggedIn() ? await getProductsPackaging({ AEMProductIDs: productsIds }) : { Packaging: undefined };

    let packagingData: (SAPProductDetail | GetPackagingItem)[] = packagingResult;
    if (!packagingData?.length) {
        packagingData = await getSAPProductDetails(materialIds)
    }

    const enrichedProducts = packagingData.reduce((acc: any[], packaging: SAPProductDetail | GetPackagingItem) => {
        const product = products.find(product => (
            product?.MaterialNumber.toString() === packaging.MaterialNumber?.toString()
        ));

        if (product) {
            acc.push({ ...product, Packagings: [packaging] });
        }
        return acc;
    }, []);

    return filterOutProducts(enrichedProducts);
}

export const enrichAEMProductsWithOutSystemData = async (products) => {
    if (!products?.length) {
        return [];
    }
    const productsIds = products.map(product => product.id);
    const materialIds: string[] = products.reduce((acc, product) => {
        if (product.packagings && Array.isArray(product.packagings)) {
            product.packagings.forEach(packaging => {
                if (packaging.code) {
                    acc.push(packaging.code);
                }
            });
        }
        return acc;
    }, []);

    const { Packaging: packagingResult } = isLoggedIn() ? await getProductsPackaging({ AEMProductIDs: productsIds }) : { Packaging: undefined };

    let packagingData: (SAPProductDetail | GetPackagingItem)[] = packagingResult;

    if (!packagingData?.length) {
        packagingData = await getSAPProductDetails(materialIds)
    }

    const enrichedProducts = packagingData.reduce((acc: any[], packaging: SAPProductDetail | GetPackagingItem) => {
        const product = products.find(product => (
            product?.packagings &&
            Array.isArray(product.packagings) && product.packagings.filter((pkg: any) => pkg.code === packaging.MaterialNumber).length > 0)
        );

        if (product) {
            if (!('skuImageURL' in packaging)) {
                product.defaultPackagingImage = product.packagings.find((pkg: any) => pkg.code === packaging.MaterialNumber).image;
            }
            acc.push({ ...product, Packagings: [packaging] });
        }
        return acc;
    }, []);

    return filterOutProducts(enrichedProducts);
}

export const filterOutProducts = (products) => {
    if (!products?.length) {
        return [];
    }
    return products.filter(product => {
        const packaging = product.Packagings?.[0];
        return !(!packaging || packaging.HasPrice === false || packaging.IsObsolete === true);
    });
  };
